import * as React from 'react';
import { FunctionComponent, useEffect, useMemo, useState } from 'react';
import BPromise from 'bluebird';
import propz from 'propz';
import { AppUser } from 'Src/views/App/App';
import { FILTER_TYPE } from 'Src/consts/table';
import { ColumnDefinition } from 'Src/helpers/table/table';
import {
  GeneralMessageRecipientPreviewItem,
  GeneralMessageRecipientType
} from 'Src/helpers/service/admin/generalMessage';
import { GeneralMessageRecipientsSelector } from 'Src/components/GeneralMessageRecipientsSelector/GeneralMessageRecipientsSelector';
import {
  GeneralMessageRecipientSelectionStateGroup,
  isGeneralMessageRecipientSelectionGroupCompleted
} from 'Src/helpers/generalMessageRecipientSelection/generalMessageRecipientSelection';
import { Tag } from 'Src/models/tag';
import { SchoolForm } from 'Src/models/form';
import { SchoolHouse } from 'Src/models/house';
import { Sport } from 'Src/models/sport';
import { getAllForms } from 'Src/helpers/service/admin/forms';
import { getAllHouses } from 'Src/helpers/service/admin/houses';
import { getAllUserTags } from 'Src/helpers/service/admin/userTags';
import { getAllSchoolActivities } from 'Src/helpers/service/admin/sports';
import {
  getSelectOptionForAge,
  getSelectOptionForForms,
  getSelectOptionForGender,
  getSelectOptionForHouses,
  getSelectOptionForSport,
  getSelectOptionForStaffs,
  getSelectOptionForTag
} from 'Src/helpers/table/select';
import { AGE_GROUPS_NAMING } from 'Src/consts/school';
import { ROLE_SERVER_TO_CLIENT_MAPPING } from 'Src/consts/user';

function getNameFromObject(item: any): string {
  if (typeof item === 'undefined' || item === null) {
    return '';
  }

  if (typeof item === 'string') {
    return item;
  }

  return item.name || item.title || item.id || '';
}

function getItemId(item: any): string {
  return String(
    propz.get(item, ['id']) ||
      propz.get(item, ['_id']) ||
      propz.get(item, ['formId']) ||
      propz.get(item, ['houseId']) ||
      propz.get(item, ['tagId']) ||
      propz.get(item, ['userTagId']) ||
      ''
  );
}

function getItemName(item: any): string {
  if (typeof item === 'undefined' || item === null) {
    return '';
  }

  if (typeof item === 'string') {
    return item;
  }

  return (
    item.name ||
    item.title ||
    item.formName ||
    item.houseName ||
    item.tagName ||
    item.text ||
    item.label ||
    item.id ||
    item._id ||
    ''
  );
}

function getNameById(list: any[], id: any): string {
  const strId = String(id || '');

  if (strId === '') {
    return '';
  }

  const item = (Array.isArray(list) ? list : []).find(listItem => {
    const possibleIds = [
      propz.get(listItem, ['id']),
      propz.get(listItem, ['_id']),
      propz.get(listItem, ['formId']),
      propz.get(listItem, ['houseId']),
      propz.get(listItem, ['tagId']),
      propz.get(listItem, ['userTagId'])
    ]
      .filter(value => typeof value !== 'undefined' && value !== null)
      .map(value => String(value));

    return possibleIds.indexOf(strId) !== -1;
  });

  return getItemName(item);
}

function getNameByIdWithoutIdFallback(list: any[], id: any): string {
  const strId = String(id || '');

  if (strId === '') {
    return '';
  }

  const item = (Array.isArray(list) ? list : []).find(listItem => {
    const possibleIds = [
      propz.get(listItem, ['id']),
      propz.get(listItem, ['_id']),
      propz.get(listItem, ['formId']),
      propz.get(listItem, ['houseId']),
      propz.get(listItem, ['tagId']),
      propz.get(listItem, ['userTagId'])
    ]
      .filter(value => typeof value !== 'undefined' && value !== null)
      .map(value => String(value));

    return possibleIds.indexOf(strId) !== -1;
  });

  if (typeof item === 'undefined' || item === null) {
    return '';
  }

  return item.name || item.title || item.formName || item.houseName || item.tagName || item.text || item.label || '';
}

function getNamesFromList(list: any[]): string {
  return (Array.isArray(list) ? list : [])
    .map(getItemName)
    .filter(name => name !== '')
    .join(', ');
}

function getRecipientBirthday(item: GeneralMessageRecipientPreviewItem): string {
  const birthday = propz.get(item, ['birthday']);

  if (typeof birthday === 'undefined' || birthday === null || birthday === '') {
    return '';
  }

  return new Date(birthday).toLocaleDateString('en-GB');
}

function getRecipientFormName(forms: SchoolForm[]) {
  return (item: GeneralMessageRecipientPreviewItem): string => {
    const itemForms = propz.get(item, ['forms'], []);

    if (Array.isArray(itemForms) && itemForms.length > 0) {
      const names = itemForms.map(getItemName).filter(name => name !== '');

      if (names.length > 0) {
        return names.join(', ');
      }
    }

    const formIds = propz.get(item, ['formIds'], []);

    if (Array.isArray(formIds) && formIds.length > 0) {
      return formIds
        .map(formId => getNameById(forms, formId))
        .filter(name => name !== '')
        .join(', ');
    }

    const form = propz.get(item, ['form']);

    if (typeof form !== 'undefined') {
      return getItemName(form);
    }

    return getNameById(forms, propz.get(item, ['formId']));
  };
}

function getRecipientHouseName(houses: SchoolHouse[]) {
  return (item: GeneralMessageRecipientPreviewItem): string => {
    const itemHouses = propz.get(item, ['houses'], []);

    if (Array.isArray(itemHouses) && itemHouses.length > 0) {
      const names = itemHouses.map(getItemName).filter(name => name !== '');

      if (names.length > 0) {
        return names.join(', ');
      }
    }

    const houseIds = propz.get(item, ['houseIds'], []);

    if (Array.isArray(houseIds) && houseIds.length > 0) {
      return houseIds
        .map(houseId => getNameById(houses, houseId))
        .filter(name => name !== '')
        .join(', ');
    }

    const house = propz.get(item, ['house']);

    if (typeof house !== 'undefined') {
      return getItemName(house);
    }

    return getNameById(houses, propz.get(item, ['houseId']));
  };
}

function getRecipientYearGroup(forms: SchoolForm[]) {
  return (item: GeneralMessageRecipientPreviewItem): string => {
    const yearGroup = propz.get(item, ['yearGroup']);

    if (typeof yearGroup !== 'undefined' && yearGroup !== null) {
      return String(yearGroup);
    }

    const formId = propz.get(item, ['formId']);
    const form = (Array.isArray(forms) ? forms : []).find(formItem => getItemId(formItem) === String(formId || ''));

    return String(propz.get(form, ['age']) || propz.get(form, ['ageGroup']) || propz.get(form, ['yearGroup']) || '');
  };
}

function getRecipientTags(tags: Tag[]) {
  return (item: GeneralMessageRecipientPreviewItem): string => {
    const itemTags = propz.get(item, ['tags'], []);
    const tagIds = propz.get(item, ['tagIds'], []);

    const objectTagNames = Array.isArray(itemTags)
      ? itemTags
          .filter(tag => typeof tag === 'object' && tag !== null)
          .map(tag => tag.name || tag.title || tag.tagName || tag.text || tag.label || '')
          .filter(name => name !== '')
      : [];

    const idsFromTags = Array.isArray(itemTags)
      ? itemTags.filter(tag => typeof tag === 'string').map(tag => String(tag))
      : [];

    const allTagIds = [...idsFromTags, ...(Array.isArray(tagIds) ? tagIds.map(tagId => String(tagId)) : [])];

    const namesFromIds = allTagIds.map(tagId => getNameByIdWithoutIdFallback(tags, tagId)).filter(name => name !== '');

    return [...objectTagNames, ...namesFromIds].filter((name, index, list) => list.indexOf(name) === index).join(', ');
  };
}

function getRecipientRole(item: GeneralMessageRecipientPreviewItem): string {
  const role = propz.get(item, ['preset'], '');
  return (
    ROLE_SERVER_TO_CLIENT_MAPPING[role] ||
    String(role || '')
      .toLowerCase()
      .replace(/^./, char => char.toUpperCase())
  );
}
function getRecipientSports(sports: Sport[]) {
  return (item: GeneralMessageRecipientPreviewItem): string => {
    if (propz.get(item, ['hasAllSportsAccess'], false)) {
      return 'All sports';
    }

    const itemSports = propz.get(item, ['sports'], []);
    const names = Array.isArray(itemSports) ? itemSports.map(getItemName).filter(name => name !== '') : [];

    if (names.length > 0) {
      return names.join(', ');
    }

    const sportIds = propz.get(item, ['sportIds'], []);
    return (Array.isArray(sportIds) ? sportIds : [])
      .map(sportId => getNameById(sports, sportId))
      .filter(name => name !== '')
      .join(', ');
  };
}

function getStudentColumns(forms: SchoolForm[], houses: SchoolHouse[], tags: Tag[]): ColumnDefinition[] {
  return [
    {
      text: 'Name',
      field: 'firstName',
      isSort: true,
      type: FILTER_TYPE.TEXT,
      accessor: ['firstName']
    },
    {
      text: 'Surname',
      field: 'lastName',
      isSort: true,
      type: FILTER_TYPE.TEXT,
      accessor: ['lastName']
    },
    {
      text: 'DOB',
      field: 'birthday',
      isSort: true,
      type: FILTER_TYPE.DATE_INTERVAL,
      accessor: getRecipientBirthday
    },
    {
      text: 'Form',
      field: 'forms',
      isSort: false,
      type: FILTER_TYPE.MULTISELECT,
      accessor: getRecipientFormName(forms)
    },
    {
      text: 'Year group',
      field: 'ages',
      isSort: false,
      type: FILTER_TYPE.MULTISELECT,
      accessor: getRecipientYearGroup(forms)
    },
    {
      text: 'House',
      field: 'houses',
      isSort: false,
      type: FILTER_TYPE.MULTISELECT,
      accessor: getRecipientHouseName(houses)
    },
    {
      text: 'Gender',
      field: 'gender',
      isSort: false,
      type: FILTER_TYPE.MULTISELECT,
      accessor: ['gender']
    },
    {
      text: 'Tag',
      field: 'tag',
      isSort: false,
      type: FILTER_TYPE.MULTISELECT,
      accessor: getRecipientTags(tags)
    }
  ];
}

function getStaffColumns(forms: SchoolForm[], houses: SchoolHouse[], sports: Sport[], tags: Tag[]): ColumnDefinition[] {
  return [
    {
      text: 'Name',
      field: 'firstName',
      isSort: true,
      type: FILTER_TYPE.TEXT,
      accessor: ['firstName']
    },
    {
      text: 'Surname',
      field: 'lastName',
      isSort: true,
      type: FILTER_TYPE.TEXT,
      accessor: ['lastName']
    },
    {
      text: 'Role',
      field: 'role',
      isSort: false,
      type: FILTER_TYPE.MULTISELECT,
      accessor: getRecipientRole
    },
    {
      text: 'Forms',
      field: 'permissionFormIds',
      isSort: false,
      type: FILTER_TYPE.MULTISELECT,
      accessor: getRecipientFormName(forms)
    },
    {
      text: 'Houses',
      field: 'permissionHouseIds',
      isSort: false,
      type: FILTER_TYPE.MULTISELECT,
      accessor: getRecipientHouseName(houses)
    },
    {
      text: 'Sports',
      field: 'permissionSportIds',
      isSort: false,
      type: FILTER_TYPE.MULTISELECT,
      accessor: getRecipientSports(sports)
    },
    {
      text: 'Tag',
      field: 'tag',
      isSort: false,
      type: FILTER_TYPE.MULTISELECT,
      accessor: getRecipientTags(tags)
    }
  ];
}

function getGovernorColumns(tags: Tag[]): ColumnDefinition[] {
  return [
    {
      text: 'Name',
      field: 'firstName',
      isSort: true,
      type: FILTER_TYPE.TEXT,
      accessor: ['firstName']
    },
    {
      text: 'Surname',
      field: 'lastName',
      isSort: true,
      type: FILTER_TYPE.TEXT,
      accessor: ['lastName']
    },
    {
      text: 'Tag',
      field: 'tag',
      isSort: false,
      type: FILTER_TYPE.MULTISELECT,
      accessor: getRecipientTags(tags)
    }
  ];
}

interface Props {
  user: AppUser;
  recipientType: GeneralMessageRecipientType;
  selectionGroup: GeneralMessageRecipientSelectionStateGroup;
  onSelectionGroupChange: (
    recipientType: GeneralMessageRecipientType,
    group: GeneralMessageRecipientSelectionStateGroup
  ) => void;
  onSelectionCompleted: (recipientType: GeneralMessageRecipientType, isCompleted: boolean) => void;
  title: string;
  availableTitle: string;
  selectedTitle: string;
  hideHeader?: boolean;
  onHelpClick?: () => void;
  helpLabel?: string;
}

export const CreateGeneralMessageRecipientsStepV2: FunctionComponent<Props> = props => {
  const {
    user,
    recipientType,
    selectionGroup,
    onSelectionGroupChange,
    onSelectionCompleted,
    title,
    availableTitle,
    selectedTitle,
    hideHeader = false,
    onHelpClick,
    helpLabel
  } = props;

  const [tags, setTags] = useState<Tag[]>([]);
  const [forms, setForms] = useState<SchoolForm[]>([]);
  const [houses, setHouses] = useState<SchoolHouse[]>([]);
  const [sports, setSports] = useState<Sport[]>([]);
  const ageGroupsNaming = propz.get(user, ['activeSchool', 'ageGroupsNaming'], AGE_GROUPS_NAMING.ENGLISH);

  useEffect(() => {
    const safely = <T extends any[]>(load: () => Promise<T>, resourceName: string): Promise<T> =>
      load().catch(firstError => {
        return new Promise<T>(resolve => {
          window.setTimeout(() => {
            load()
              .then(resolve)
              .catch(error => {
                console.error(`General message recipient ${resourceName} could not be loaded`, error || firstError);
                resolve([] as T);
              });
          }, 500);
        });
      });

    if (recipientType === 'students') {
      BPromise.all([
        safely(() => getAllHouses(user), 'houses'),
        safely(() => getAllForms(user), 'forms'),
        safely(() => getAllSchoolActivities(user), 'activities'),
        safely(() => getAllUserTags(user), 'tags')
      ]).then(([houses, forms, sports, tags]) => {
        setHouses(houses);
        setForms(forms);
        setSports(sports);
        setTags(tags);
      });
      return;
    }

    if (recipientType === 'staffs') {
      BPromise.all([
        safely(() => getAllHouses(user), 'houses'),
        safely(() => getAllForms(user), 'forms'),
        safely(() => getAllSchoolActivities(user), 'activities'),
        safely(() => getAllUserTags(user), 'tags')
      ]).then(([houses, forms, sports, tags]) => {
        setHouses(houses);
        setForms(forms);
        setSports(sports);
        setTags(tags);
      });
      return;
    }

    safely(() => getAllUserTags(user), 'tags').then(tags => setTags(tags));
  }, [recipientType, user]);

  const columns = useMemo(() => {
    switch (recipientType) {
      case 'students':
        return getStudentColumns(forms, houses, tags);
      case 'staffs':
        return getStaffColumns(forms, houses, sports, tags);
      case 'governors':
        return getGovernorColumns(tags);
      default:
        return getStudentColumns(forms, houses, tags);
    }
  }, [forms, houses, recipientType, sports, tags]);

  const filterOptions = useMemo(() => {
    if (recipientType === 'students') {
      return {
        forms: getSelectOptionForForms(forms),
        ages: getSelectOptionForAge(forms, ageGroupsNaming),
        houses: getSelectOptionForHouses(houses),
        gender: getSelectOptionForGender(),
        tag: getSelectOptionForTag(tags)
      };
    }

    if (recipientType === 'staffs') {
      return {
        role: getSelectOptionForStaffs(),
        permissionFormIds: getSelectOptionForForms(forms),
        permissionHouseIds: getSelectOptionForHouses(houses),
        permissionSportIds: getSelectOptionForSport(sports),
        tag: getSelectOptionForTag(tags)
      };
    }

    return {
      tag: getSelectOptionForTag(tags)
    };
  }, [ageGroupsNaming, forms, houses, recipientType, sports, tags]);

  const serverFieldOptions = useMemo(() => {
    if (recipientType === 'students') {
      return {
        forms
      };
    }

    return undefined;
  }, [forms, recipientType]);

  const onChange = (group: GeneralMessageRecipientSelectionStateGroup) => {
    onSelectionGroupChange(recipientType, group);
    onSelectionCompleted(recipientType, isGeneralMessageRecipientSelectionGroupCompleted(group));
  };

  return (
    <div className={hideHeader ? 'gmRecipientStep gmRecipientStep--compact' : 'mt-3'}>
      <GeneralMessageRecipientsSelector
        key={recipientType}
        user={user}
        recipientType={recipientType}
        columns={columns}
        selectionGroup={selectionGroup}
        onSelectionGroupChange={onChange}
        title={hideHeader ? undefined : title}
        availableTitle={availableTitle}
        selectedTitle={selectedTitle}
        filterOptions={filterOptions}
        serverFieldOptions={serverFieldOptions}
        forms={forms}
        houses={houses}
        onHelpClick={onHelpClick}
        helpLabel={helpLabel}
      />
    </div>
  );
};

CreateGeneralMessageRecipientsStepV2.displayName = 'CreateGeneralMessageRecipientsStepV2';
